import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import html2pdf from 'html2pdf.js';
import api from '../services/api';
import '../styles/ClientPrint.css';

const ClientPrint = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const printRef = useRef();
  const [loading, setLoading] = useState(true);
  const [client, setClient] = useState(null);
  const [services, setServices] = useState({ services: [], sub_services: [] });

  useEffect(() => {
    loadClientData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadClientData = async () => {
    try {
      setLoading(true);
      
      // Load client details
      const clientResponse = await api.getClient(id);
      setClient(clientResponse.data);
      
      // Load client services
      try {
        const servicesResponse = await api.getClientServices(clientResponse.data.client_id);
        if (servicesResponse.success && servicesResponse.data) {
          const servicesData = {
            services: Array.isArray(servicesResponse.data.services) ? servicesResponse.data.services : [],
            sub_services: Array.isArray(servicesResponse.data.sub_services) ? servicesResponse.data.sub_services : []
          };
          setServices(servicesData);
        } else {
          setServices({ services: [], sub_services: [] });
        }
      } catch (err) {
        console.error('Failed to load services:', err);
        setServices({ services: [], sub_services: [] });
      }
    } catch (err) {
      console.error('Failed to load client:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: client ? `Order_Form_${client.client_id}` : `Order_Form_${id}`,
    pageStyle: `
      @page {
        size: A4;
        margin: 1cm;
      }
      @media print {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
    `,
    onBeforeGetContent: () => {
      if (!printRef.current) {
        console.error('Print content not found');
        return Promise.resolve();
      }
      return Promise.resolve();
    },
    onPrintError: (error) => {
      console.error('Print error:', error);
      alert('Error printing. Please try again.');
    },
  });

  const handlePrintClick = () => {
    console.log('Print button clicked');
    
    if (!printRef.current) {
      console.error('Print content not found');
      alert('Print content is not ready. Please wait for the page to load.');
      return;
    }
    
    console.log('Calling handlePrint, ref exists:', !!printRef.current);
    try {
      handlePrint();
    } catch (error) {
      console.error('Print error:', error);
      window.print();
    }
  };

  const handleDownloadPDF = () => {
    if (!printRef.current) {
      alert('Form content is not ready. Please wait for the page to load.');
      return;
    }

    const element = printRef.current;
    const filename = client ? `Order_Form_${client.client_id}.pdf` : `Order_Form_${id}.pdf`;

    // Show loading message
    const loadingMessage = document.createElement('div');
    loadingMessage.style.cssText = 'position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);background:#1a237e;color:#fff;padding:20px 40px;border-radius:8px;z-index:99999;font-size:16px;';
    loadingMessage.textContent = 'Loading images...';
    document.body.appendChild(loadingMessage);

    // Wait for all images to load before generating PDF
    const images = element.querySelectorAll('img');
    const imagePromises = Array.from(images).map((img) => {
      if (img.complete) {
        return Promise.resolve();
      }
      return new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = () => {
          console.warn('Image failed to load:', img.src);
          resolve(); // Continue even if image fails
        };
        // Timeout after 5 seconds
        setTimeout(() => {
          console.warn('Image load timeout:', img.src);
          resolve(); // Continue even if timeout
        }, 5000);
      });
    });

    Promise.all(imagePromises)
      .then(() => {
        loadingMessage.textContent = 'Generating PDF...';
        
        // Add compact class to apply PDF styles
        element.classList.add('pdf-compact');

        const opt = {
          margin: [0.5, 0.5, 0.5, 0.5],
          filename: filename,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { 
            scale: 2,
            useCORS: false,
            allowTaint: true,
            logging: false,
            letterRendering: true,
            backgroundColor: '#ffffff',
            windowWidth: element.scrollWidth,
            windowHeight: element.scrollHeight,
            imageTimeout: 15000
          },
          jsPDF: { 
            unit: 'cm', 
            format: 'a4', 
            orientation: 'portrait',
            compress: true
          },
          pagebreak: { 
            mode: ['avoid-all', 'css', 'legacy'],
            before: '.page-break-before',
            after: '.page-break-after',
            avoid: ['.form-section', '.terms-section']
          }
        };

        return html2pdf()
          .set(opt)
          .from(element)
          .save();
      })
      .then(() => {
        document.body.removeChild(loadingMessage);
        // Remove the compact class after PDF generation
        element.classList.remove('pdf-compact');
      })
      .catch((error) => {
        console.error('PDF generation error:', error);
        document.body.removeChild(loadingMessage);
        // Remove the compact class on error too
        element.classList.remove('pdf-compact');
        alert('Error generating PDF. Please try again or use the print option.');
      });
  };

  if (loading) {
    return (
      <div className="print-loading">
        <p>Loading order form...</p>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="print-error">
        <p>Client not found</p>
      </div>
    );
  }

  return (
    <div className="order-form-print">
      <div className="print-actions no-print">
        <button onClick={handleDownloadPDF} className="btn btn-primary" disabled={loading || !client}>
          📥 Download PDF
        </button>
        <button onClick={handlePrintClick} className="btn btn-primary" disabled={loading || !client}>
          🖨️ Print Order Form
        </button>
        <button onClick={() => navigate('/clients')} className="btn btn-secondary">
          Close
        </button>
      </div>

      <div ref={printRef} className="container">
        {/* Single Page Form */}
        <main className="form-page">
          {/* Header Section */}
          <header className="form-header">
            <div className="header-left">
              <div className="logo-section">
                <img 
                  src="https://onerankdigital.com/wp-content/uploads/2023/10/FINAL-1024x347.png"
                  alt="OneRank Digital Logo" 
                  className="company-logo"
                  onLoad={() => console.log('Logo image loaded')}
                  onError={(e) => {
                    console.error('Logo image failed to load:', e);
                    // Try to reload without CORS restrictions
                    e.target.src = e.target.src + '?t=' + Date.now();
                  }}
                />
              </div>
              <address className="contact-info">
                <div className="contact-item">
                  <span className="icon" aria-hidden="true">
                    {/* Email Icon SVG */}
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="#e91e63" viewBox="0 0 24 24">
                      <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.89 2 1.99 2H20c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                    </svg>
                  </span>
                  <a href="mailto:support@onerankdigital.com">support@onerankdigital.com</a>
                </div>
                <div className="contact-item">
                  <span className="icon" aria-hidden="true">
                    {/* Phone Icon SVG */}
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="#e91e63" viewBox="0 0 24 24">
                      <path d="M6.62 10.79a15.054 15.054 0 006.59 6.59l2.2-2.2a1 1 0 011.11-.21c1.21.49 2.53.76 3.88.76a1 1 0 011 1v3.5a1 1 0 01-1 1C10.07 22 2 13.93 2 4a1 1 0 011-1H6.5a1 1 0 011 1c0 1.35.27 2.67.76 3.88a1 1 0 01-.21 1.11l-2.2 2.2z"/>
                    </svg>
                  </span>
                  <a href="tel:8007231573">80072 31573</a> / <a href="tel:7506555044">75065 55044</a>
                </div>
                <div className="contact-item">
                  <span className="icon" aria-hidden="true">
                    {/* Location Icon SVG */}
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="#e91e63" viewBox="0 0 24 24">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 110-5 2.5 2.5 0 010 5z"/>
                    </svg>
                  </span>
                  <span className="text-dark">Ram Tekdi Path, Ram Tekdi, Sewree, Mumbai - 400015</span>
                </div>
                <div className="contact-item">
                  <span className="icon" aria-hidden="true">
                    {/* Location Icon SVG */}
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="#e91e63" viewBox="0 0 24 24">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 110-5 2.5 2.5 0 010 5z"/>
                    </svg>
                  </span>
                  <span className="text-dark">Pune: Megapolis, Hinjewadi Phase-3, Pune - 411057</span>
                </div>
              </address>
            </div>

            <div className="header-right">
              <h1 className="form-title">Order Form</h1>
              <p className="form-subtitle">Trusted Digital Marketing Company In India</p>

              <div className="form-details-box">
                <div className="form-detail-item">
                  <label>Customer No:</label>
                  <input type="text" value={client.client_id || ''} readOnly />
                </div>
                <div className="form-detail-item">
                  <label>Date:</label>
                  <input type="text" value={new Date(client.order_date || client.created_at).toLocaleDateString('en-GB')} readOnly />
                </div>
                <div className="form-detail-item">
                  <label>Total Package:</label>
                  <input type="text" value={`₹${parseFloat(client.total_amount || 0).toFixed(2)}`} readOnly />
                </div>
              </div>
            </div>
          </header>

          {/* Main Form */}
          <form className="main-form">
            {/* CLIENT INFORMATION */}
            <section className="form-section">
              <h2 className="section-heading">Client Information</h2>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Company Name:</label>
                  <input type="text" value={client.client_name || ''} readOnly />
                </div>

                <div className="form-row">
                  <div className="form-group half-width">
                    <label>Contact Person:</label>
                    <input type="text" value={client.person_name || ''} readOnly />
                  </div>
                  <div className="form-group half-width">
                    <label>Designation:</label>
                    <input type="text" value={client.designation || 'N/A'} readOnly />
                  </div>
                </div>

                <div className="form-group full-width">
                  <label>Address:</label>
                  <textarea rows="3" value={client.address || ''} readOnly></textarea>
                </div>

                <div className="form-row">
                  <div className="form-group half-width">
                    <label>Contact No.:</label>
                    <input type="tel" value={client.phone || ''} readOnly />
                  </div>
                  <div className="form-group half-width">
                    <label>E-mail:</label>
                    <input type="email" value={client.email || ''} readOnly />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group half-width">
                    <label>Domain Name:</label>
                    <input 
                      type="text" 
                      value={Array.isArray(client.domains) ? client.domains.join(', ') : client.domains || 'N/A'} 
                      readOnly 
                    />
                  </div>
                  <div className="form-group half-width">
                    <label>GSTIN No.:</label>
                    <input type="text" value={client.gstin_no || 'N/A'} readOnly />
                  </div>
                </div>
              </div>
            </section>

            {/* SERVICES */}
            <section className="form-section">
              <h2 className="section-heading">Select Services</h2>

              <div className="services-grid">
                {/* Domain & Hosting */}
                <div className="service-column">
                  <div className="service-category">Domain & Hosting</div>
                  <fieldset className="checkbox-list">
                    <legend className="sr-only">Domain & Hosting</legend>
                    {(services.services || []).filter(s => (s.service_name || s.name || '').toLowerCase().includes('domain') || (s.service_name || s.name || '').toLowerCase().includes('hosting')).length > 0 ? (
                      services.services.filter(s => (s.service_name || s.name || '').toLowerCase().includes('domain') || (s.service_name || s.name || '').toLowerCase().includes('hosting')).map((service, index) => (
                        <label key={service.id || index} className="checkbox-item">
                          <input type="checkbox" checked readOnly disabled />
                          <span>{service.service_name || service.name}</span>
                        </label>
                      ))
                    ) : null}
                    {(services.sub_services || []).filter(s => (s.sub_service_name || s.name || '').toLowerCase().includes('pop')).map((subService, index) => (
                      <label key={subService.id || index} className="checkbox-item">
                        <input type="checkbox" checked readOnly disabled />
                        <span>{subService.sub_service_name || subService.name}</span>
                        {subService.quantity > 1 && <input type="number" value={subService.quantity} readOnly disabled style={{ width: '60px', marginLeft: '8px' }} />}
                      </label>
                    ))}
                  </fieldset>
                </div>

                {/* Web Design */}
                <div className="service-column">
                  <div className="service-category">Web Design</div>
                  <fieldset className="checkbox-list">
                    <legend className="sr-only">Web Design</legend>
                    {(services.services || []).filter(s => {
                      const name = (s.service_name || s.name || '').toLowerCase();
                      return name.includes('website') || name.includes('web') || name.includes('design') || name.includes('development') || name.includes('app');
                    }).map((service, index) => (
                      <label key={service.id || index} className="checkbox-item">
                        <input type="checkbox" checked readOnly disabled />
                        <span>{service.service_name || service.name}</span>
                      </label>
                    ))}
                  </fieldset>
                </div>

                {/* SEO */}
                <div className="service-column">
                  <div className="service-category">SEO</div>
                  <fieldset className="checkbox-list">
                    <legend className="sr-only">SEO</legend>
                    {(services.services || []).filter(s => {
                      const name = (s.service_name || s.name || '').toLowerCase();
                      return name.includes('seo') || name.includes('google') || name.includes('ppc') || name.includes('ads');
                    }).map((service, index) => (
                      <label key={service.id || index} className="checkbox-item">
                        <input type="checkbox" checked readOnly disabled />
                        <span>{service.service_name || service.name}</span>
                      </label>
                    ))}
                  </fieldset>
                </div>

                {/* Additional Services */}
                <div className="service-column">
                  <div className="service-category">Additional Services</div>
                  <fieldset className="checkbox-list">
                    <legend className="sr-only">Additional Services</legend>
                    {(services.services || []).filter(s => {
                      const name = (s.service_name || s.name || '').toLowerCase();
                      return !name.includes('domain') && !name.includes('hosting') && !name.includes('website') && !name.includes('web') && !name.includes('seo') && !name.includes('google');
                    }).map((service, index) => (
                      <label key={service.id || index} className="checkbox-item">
                        <input type="checkbox" checked readOnly disabled />
                        <span>{service.service_name || service.name}</span>
                      </label>
                    ))}
                  </fieldset>
                </div>
              </div>
            </section>

            {/* Special Guidelines */}
            <section className="form-section">
              <div className="form-group full-width">
                <label>Specific Guidelines (Website & SEO):</label>
                <textarea rows="5" value={client.specific_guidelines || ''} readOnly></textarea>
              </div>
            </section>

            {/* Special Instructions Form */}
            <div className="special-instructions">
              {/* SEO */}
              {(client.seo_keyword_range || client.seo_location || client.seo_keywords_list) && (
                <fieldset className="instructions-section">
                  <legend className="instruction-category">SEARCH ENGINE OPTIMIZATION (SEO)</legend>

                  {client.seo_keyword_range && (
                    <div className="form-group inline-group">
                      <label>Keywords:</label>
                      <span>{client.seo_keyword_range}</span>
                    </div>
                  )}

                  {client.seo_location && (
                    <div className="form-group inline-group">
                      <label>Location:</label>
                      <span>{client.seo_location}</span>
                    </div>
                  )}

                  {client.seo_keywords_list && (
                    <div className="form-group">
                      <label>Top Products / Keywords:</label>
                      <textarea rows="8" value={client.seo_keywords_list || ''} readOnly></textarea>
                    </div>
                  )}
                </fieldset>
              )}

              {/* GOOGLE ADWORDS */}
              {(client.adwords_keywords || client.adwords_location || client.adwords_keywords_list) && (
                <fieldset className="instructions-section">
                  <legend className="instruction-category">GOOGLE ADWORDS</legend>
                  {client.adwords_keywords && (
                    <div className="form-group">
                      <label>Number of Keywords:</label>
                      <input type="text" value={client.adwords_keywords || ''} readOnly />
                      {client.adwords_period && (
                        <input type="text" value={client.adwords_period || ''} placeholder="(Monthly / Quarterly)" readOnly style={{ marginLeft: '10px' }} />
                      )}
                    </div>
                  )}
                  {client.adwords_location && (
                    <div className="form-group">
                      <label>Location:</label>
                      <input type="text" value={client.adwords_location || ''} readOnly />
                    </div>
                  )}
                  {client.adwords_keywords_list && (
                    <div className="form-group">
                      <label>Keywords:</label>
                      <textarea rows="4" value={client.adwords_keywords_list || ''} readOnly></textarea>
                    </div>
                  )}
                </fieldset>
              )}

              {/* SPECIAL GUIDELINES */}
              {client.special_guidelines && (
                <fieldset className="instructions-section">
                  <legend className="instruction-category">SPECIAL GUIDELINES</legend>
                  <div className="form-group">
                    <textarea rows="5" value={client.special_guidelines || ''} readOnly></textarea>
                  </div>
                </fieldset>
              )}
            </div>

            {/* Payment Mode */}
            <section className="form-section">
              <h2 className="section-heading">Mode of Payment</h2>
              <div className="payment-options">
                {client.payment_mode && (
                  <span>{client.payment_mode.replace(/-/g, ' ').toUpperCase()}</span>
                )}
              </div>
            </section>
          </form>

          {/* Terms & Conditions */}
          <section className="terms-section">
            <h2 className="terms-title">Terms & Conditions</h2>
            <ol className="terms-list">
              <li><strong>The information contained on the website is solely the responsibility of the client's signatory, and the signatory hereby agrees to fully indemnify One Rank Digital in this regard as and when any claims, demands, etc.</strong></li>
              <li><strong>The website will be placed on the signatory's domain name within one month of receiving the complete website matter, photos, designs, and so on, as well as receipt of the full payment.</strong></li>
              <li><strong>Please make sure that the website content, product information, and photos you provide are your own.</strong></li>
              <li><strong>If a legal dispute arises in the future, our company will not be held liable under any circumstances.</strong></li>
              <li><strong>If you do not renew your web package after one year, we will not be responsible for domain names, website content, or mailing services.</strong></li>
              <li><strong>Any applicable government taxes or levies will be assessed.</strong></li>
              <li><strong>Scope of Work:</strong> One Rank Digital agrees to provide website design and promotion services as detailed in the project proposal or agreement.</li>
              <li><strong>Client Responsibilities:</strong> The client agrees to provide all necessary materials, information, and approvals required for the timely completion of the project. Timely communication and feedback from the client are essential for the progress of the project.</li>
              <li><strong>Payment Terms:</strong> The client agrees to pay 60% of the total project cost as an upfront deposit before the commencement of work. Milestone payments will be specified in the project proposal, with the final payment due upon project completion. Late payments may incur additional charges, and the Company reserves the right to suspend work until outstanding payments are received.</li>
              <li><strong>Intellectual Property:</strong> Upon full payment, the client will own the intellectual property rights to the final website design. The Company retains the right to use non-proprietary elements for promotional purposes.</li>
              <li><strong>Timeline and Delays:</strong> A project timeline will be provided in the project proposal. Delays caused by the client may result in an extension of the project timeline.</li>
              <li><strong>Termination of Services:</strong> Either party may terminate the agreement in writing if the other party breaches its obligations. Termination fees may apply.</li>
              <li><strong>Confidentiality:</strong> Both parties agree to keep confidential information exchanged during the project confidential.</li>
              <li><strong>Hosting and Domain:</strong> If hosting and domain services are provided by the Company, the client agrees to adhere to the terms and conditions of the hosting provider.</li>
              <li><strong>Legal Compliance:</strong> The client is responsible for ensuring the website complies with applicable laws and regulations.</li>
              <li><strong>Updates to Terms and Conditions:</strong> The Company reserves the right to update these terms and conditions. Clients will be notified of any changes.</li>
            </ol>
          </section>

          {/* Footer */}
          <footer className="form-footer">
            <div className="footer-left">
              <div className="footer-block">
                <label className="footer-label">Name & Designation:</label>
                <div className="signature-line">{client.signature_name || ''}</div>
              </div>
              <div className="footer-block">
                <label className="footer-label">Signature:</label>
                {client.esignature_data ? (
                  <div style={{ marginBottom: '10px' }}>
                    <img 
                      src={client.esignature_data} 
                      alt="Client Signature" 
                      style={{ maxWidth: '100%', height: 'auto', border: '1px solid #333' }}
                    />
                  </div>
                ) : (
                  <div className="signature-line">{client.signature_text || ''}</div>
                )}
              </div>
            </div>

            <div className="footer-right">
              <div className="footer-block">
                <label className="footer-label">For One Rank Digital</label>
                <div className="signature-line"></div>
                <p style={{ fontSize: '14px', color: '#333', fontWeight: 'bold', marginTop: '10px' }}>Authorized Signatory</p>
              </div>
            </div>
          </footer>

          {/* Bottom Footer */}
          <div className="bottom-footer">
            <div className="footer-curve"></div>
            <div className="footer-content">
              <span className="globe-icon">🌐</span>
              <span className="website-url">www.onerankdigital.com</span>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ClientPrint;